import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import ManagerSidebar from './ManagerSidebar';
import { useCrmShellPresentation } from '../crm/CrmSidebarContext';
import '../../styles/manager-redesign.css';

import { UserProfileProvider } from '../../contexts/UserProfileProvider';
import {
  createScalarDraftAdapter,
  useNavigationScope,
  useNavigationScrollRef,
  useSessionViewState,
} from '../../navigation';
import {
  ReceptionLayoutContext,
  type ReceptionLayoutContextType,
  type ReceptionLayoutScrollOwner,
} from '../../pages/reception/ReceptionLayoutContext';

interface Props {
  children?: React.ReactNode;
}

const ManagerPageWrapper: React.FC<Props> = ({ children }) => {
  const location = useLocation();
  const navigationScope = useNavigationScope();
  const { registerScrollTarget } = useCrmShellPresentation();
  const sidebarViewAdapter = React.useMemo(() => createScalarDraftAdapter({
    collapsed: typeof window !== 'undefined'
      ? window.matchMedia('(max-width: 1023px)').matches
      : false,
  }), []);
  const {
    value: sidebarView,
    setValue: setSidebarView,
  } = useSessionViewState({
    scopeId: navigationScope.scopeId,
    userId: String(navigationScope.userId),
    workspace: 'manager',
    routeId: 'manager.shell',
    purpose: 'sidebar-view',
    version: 1,
  }, sidebarViewAdapter);
  const collapsed = sidebarView.collapsed;
  const [mobile, setMobile] = React.useState(() => window.matchMedia('(max-width: 1023px)').matches);
  const [title, setTitle] = React.useState('Manager');
  const [scrollOwner, setScrollOwner] = React.useState<(ReceptionLayoutScrollOwner & { owner: symbol }) | null>(null);
  const registerScrollOwner = React.useCallback<ReceptionLayoutContextType['registerScrollOwner']>((owner, nextOwner) => {
    setScrollOwner({ ...nextOwner, owner });
    return () => {
      setScrollOwner((current) => current?.owner === owner ? null : current);
    };
  }, []);
  const mainScrollRef = useNavigationScrollRef<HTMLElement>({
    key: scrollOwner?.key ?? 'manager.shell-main',
    ready: scrollOwner?.ready ?? true,
    viewKey: scrollOwner?.viewKey ?? `${location.pathname}${location.search}`,
  });
  // Keep the restoration ref and DOM node intact; the header is only an observer.
  const shellScrollRef = React.useCallback((node: HTMLElement | null) => {
    mainScrollRef(node);
    registerScrollTarget(node);
  }, [mainScrollRef, registerScrollTarget]);
  const layoutContext = React.useMemo<ReceptionLayoutContextType>(() => ({
    setTitle,
    setNoPadding: () => undefined,
    registerScrollOwner,
    initialized: true,
  }), [registerScrollOwner]);

  React.useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1023px)');
    const syncCollapsedState = () => {
      setMobile(mediaQuery.matches);
      setSidebarView({ collapsed: mediaQuery.matches });
    };

    mediaQuery.addEventListener('change', syncCollapsedState);
    return () => mediaQuery.removeEventListener('change', syncCollapsedState);
  }, [setSidebarView]);

  React.useEffect(() => {
    document.title = title ? `${title} | Manager` : 'Manager';
  }, [title]);

  return (
    <UserProfileProvider>
      <ReceptionLayoutContext.Provider value={layoutContext}>
        <div className={`manager-root crm-manager-frame ${collapsed ? 'manager-root--collapsed' : ''}`}>
          <ManagerSidebar
            collapsed={collapsed}
            mobile={mobile}
            onToggle={() => setSidebarView({ collapsed: !collapsed })}
            onNavigate={() => { if (mobile) setSidebarView({ collapsed: true }); }}
          />
          <main
            ref={shellScrollRef}
            className="manager-main"
          >
            {children ? children : <Outlet />}
          </main>
        </div>
      </ReceptionLayoutContext.Provider>
    </UserProfileProvider>
  );
};

export default ManagerPageWrapper;
